import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import IconClose from '@bubble-ui/icon-close';
import IconDone from '@bubble-ui/icon-done';
import IconError from '@bubble-ui/icon-error';
import IconInformation from '@bubble-ui/icon-information';
import IconWarning from '@bubble-ui/icon-warning';
import SpacerVertical from '@bubble-ui/atom-spacer-vertical';
import TouchableIcon from '@bubble-ui/molecule-touchable-icon';
import Typography from '@bubble-ui/molecule-typography';
import View from '@bubble-ui/primitive-view';
import { useViewSize } from '@bubble-ui/hook-use-view-size';
import { useAnimationControl } from '@bubble-ui/provider-animation-control';
import { isNative } from '@bubble-ui/util-platform';
import { ANIMATION_DURATION_FADE_OUT } from './constants';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { Portal } from './Portal';
var getDisplayName = element => {
  var _element$type;
  return (// @ts-expect-error
    element === null || element === void 0 ? void 0 : (_element$type = element.type) === null || _element$type === void 0 ? void 0 : _element$type.displayName
  );
};
var Takeover = _ref => {
  var {
    'aria-live': ariaLive,
    breakpoint,
    children,
    onClose,
    position: positionProp,
    role,
    status = 'default',
    title,
    UNSAFE_containerProps
  } = _ref;
  var {
    disableAnimations
  } = useAnimationControl();
  var designContext = useDesignContext(defaultDesignTokens);
  var [viewProps, {
    width
  }] = useViewSize();
  var [animationState, setAnimationState] = useState();
  var [closeEvent, setCloseEvent] = useState();
  var FINAL_ANIMATION_DURATION_FADE_OUT = disableAnimations ? 0 : ANIMATION_DURATION_FADE_OUT;
  var position = isNative ? undefined : positionProp;
  useEffect(() => {
    if (animationState !== 'fade-out') return;
    var fadeOutTimeout = setTimeout(() => {
      setAnimationState('end');
      onClose(closeEvent);
    }, FINAL_ANIMATION_DURATION_FADE_OUT);
    return () => {
      clearTimeout(fadeOutTimeout);
    };
  }, [animationState, closeEvent, FINAL_ANIMATION_DURATION_FADE_OUT, onClose, position]);
  var IconType = status === 'error' ? IconError : status === 'warning' ? IconWarning : status === 'success' ? IconDone : IconInformation;
  var {
    rootStyle,
    iconWrapperStyle,
    contentWrapperStyle,
    closeWrapperStyle,
    portalStyle
  } = getDefaultStyleSheet({
    animationState,
    breakpoint,
    designContext,
    disableAnimations,
    status,
    width
  });
  var TakeoverComponent = /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, {
    "aria-live": ariaLive,
    role: role,
    style: rootStyle
  }, viewProps), /*#__PURE__*/React.createElement(View, {
    style: iconWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(IconType, {
    colorToken: "takeover/text-color"
  }))), /*#__PURE__*/React.createElement(View, {
    style: contentWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, title && /*#__PURE__*/React.createElement(Typography, {
    colorToken: "takeover/text-color",
    textToken: "takeover/title-text-style"
  }, title), React.Children.map(children, (child, i) => {
    var _getDisplayName;
    return /*#__PURE__*/React.createElement(React.Fragment, null, (i > 0 || title) && /*#__PURE__*/React.createElement(SpacerVertical, {
      spaceToken: "space/100"
    }), /*#__PURE__*/React.isValidElement(child) ? getDisplayName(child) === 'Typography' ? /*#__PURE__*/React.cloneElement(child, {
      textToken: child.props.textToken || 'takeover/text-style',
      colorToken: child.props.colorToken || 'takeover/text-color'
    }) : getDisplayName(child) === 'List' ? /*#__PURE__*/React.cloneElement(child, {
      textToken: child.props.textToken || 'takeover/text-style',
      colorToken: child.props.colorToken || 'takeover/text-color',
      noStartPadding: child.props.noStartPadding !== undefined ? child.props.noStartPadding : true,
      itemSpaceToken: child.props.itemSpaceToken || 'space/100'
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, (i > 0 || title) && /*#__PURE__*/React.createElement(SpacerVertical, {
      spaceToken: "space/50"
    }), getDisplayName(child) === 'Link' ? /*#__PURE__*/React.cloneElement(child, {
      textToken: child.props.textToken || 'takeover/text-style',
      colorToken: child.props.colorToken || 'takeover/text-color',
      isUnderlined: child.props.isUnderlined !== undefined ? child.props.isUnderlined : true
    }) : (_getDisplayName = getDisplayName(child)) !== null && _getDisplayName !== void 0 && _getDisplayName.startsWith('Button') ? /*#__PURE__*/React.createElement(View, {
      style: {
        alignItems: 'flex-start'
      }
    }, child) : child) : /*#__PURE__*/React.createElement(Typography, {
      textToken: "takeover/text-style",
      colorToken: "takeover/text-color"
    }, child));
  }))), /*#__PURE__*/React.createElement(View, {
    style: closeWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(TouchableIcon, {
    colorToken: "takeover/text-color",
    Icon: IconClose,
    onPress: e => {
      if (position) {
        setCloseEvent(e);
        setAnimationState('fade-out');
      } else {
        onClose(e);
      }
    }
  }))));
  return position ? animationState !== 'end' ? /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(View, {
    style: portalStyle
  }, TakeoverComponent)) : null : TakeoverComponent;
};
var Row = _ref2 => {
  var {
    children
  } = _ref2;
  var designContext = useDesignContext(defaultDesignTokens);
  return /*#__PURE__*/React.createElement(View, {
    style: {
      flexDirection: 'row',
      gap: designContext.get('space/100').value()
    }
  }, children);
};
Row.displayName = 'Takeover.Row';
Takeover.Row = Row;
Takeover.displayName = 'Takeover';
export { Takeover };
//# sourceMappingURL=index.js.map